import React from 'react';
import { motion } from 'framer-motion';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Calendar, User, Building, CheckSquare, Clock, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Deliverable } from '@/types/deliverables';
import { DeliverableCategoryBadge } from './DeliverableCategoryBadge';

interface DeliverableKanbanCardProps {
  deliverable: Deliverable;
  onClick: (deliverable: Deliverable) => void;
  checklistProgress?: { completed: number; total: number };
}

export const DeliverableKanbanCard: React.FC<DeliverableKanbanCardProps> = ({
  deliverable,
  onClick,
  checklistProgress
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: deliverable.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Calculate due date status
  const getDueDateStatus = () => {
    if (deliverable.status === 'DELIVERED' || deliverable.status === 'VERIFIED') {
      return { className: 'text-green-600 bg-green-50', label: 'Completed', icon: null };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const committedDate = new Date(deliverable.committedDate);
    committedDate.setHours(0, 0, 0, 0);

    const diffTime = committedDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return {
        className: 'text-red-600 bg-red-50',
        label: `${Math.abs(diffDays)}d overdue`,
        icon: <AlertTriangle className="w-3 h-3" />
      };
    } else if (diffDays === 0) {
      return {
        className: 'text-orange-600 bg-orange-50',
        label: 'Due today',
        icon: <Clock className="w-3 h-3" />
      };
    } else if (diffDays <= 7) {
      return {
        className: 'text-yellow-600 bg-yellow-50',
        label: `${diffDays}d left`,
        icon: <Clock className="w-3 h-3" />
      };
    } else {
      return {
        className: 'text-gray-600 bg-gray-50',
        label: new Date(deliverable.committedDate).toLocaleDateString(),
        icon: <Calendar className="w-3 h-3" />
      };
    }
  };

  const dueDateStatus = getDueDateStatus();

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={cn(
        'group bg-white rounded-lg border border-gray-200 p-4 cursor-pointer',
        'hover:border-blue-300 hover:shadow-md transition-all duration-200',
        isDragging && 'shadow-xl border-blue-400 rotate-2 z-50'
      )}
      onClick={() => onClick(deliverable)}
    >
      {/* Client Info */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-sm text-gray-600 mb-1">
            <Building className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate font-medium">{deliverable.lead?.company || 'Unknown'}</span>
          </div>
          <p className="text-xs text-gray-500 truncate">
            {deliverable.lead?.name || 'Unknown Lead'}
          </p>
        </div>
        <DeliverableCategoryBadge category={deliverable.category} className="flex-shrink-0" />
      </div>

      {/* Deliverable Title */}
      <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2 text-sm">
        {deliverable.title}
      </h4>

      {/* Description Preview */}
      {deliverable.description && (
        <p className="text-xs text-gray-500 line-clamp-2 mb-3">
          {deliverable.description}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        {/* Due Date Badge */}
        <div className={cn(
          'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
          dueDateStatus.className
        )}>
          {dueDateStatus.icon}
          <span>{dueDateStatus.label}</span>
        </div>

        {/* Checklist Progress */}
        {checklistProgress && checklistProgress.total > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <CheckSquare className="w-3.5 h-3.5" />
            <span>{checklistProgress.completed}/{checklistProgress.total}</span>
            <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${(checklistProgress.completed / checklistProgress.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Assigned User Avatar */}
        {deliverable.assignedToUser && (
          <div
            className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center"
            title={deliverable.assignedToUser.name}
          >
            <span className="text-white text-xs font-medium">
              {deliverable.assignedToUser.name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}

        {!deliverable.assignedToUser && (
          <div
            className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center"
            title="Unassigned"
          >
            <User className="w-3 h-3 text-gray-400" />
          </div>
        )}
      </div>
    </motion.div>
  );
};
